import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Gift, X } from 'lucide-react';
import { ChangelogContent } from './ChangelogContent';
import type { ChangelogEntry } from '../data/changelog';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { openExternalUrl } from '../../help-feedback';
import { reviewUrl } from '../../review-prompt/reviewPromptConstants';

interface WhatsNewDialogProps {
  open: boolean;
  entries: ChangelogEntry[];
  onDismiss: () => void;
}

export function WhatsNewDialog({ open, entries, onDismiss }: WhatsNewDialogProps) {
  const { track } = useAnalytics();

  const handleReviewLinkClick = () => {
    track('feature:review_link_click', { source: 'whats-new' });
    openExternalUrl(reviewUrl('whats-new'));
  };

  const dismiss = (via: 'x' | 'got_it') => {
    track('feature:whats_new_dismiss', { via });
    onDismiss();
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onDismiss(); }}>
      {/* Closes only through the X or "Got it": a stray click on the backdrop
          or Escape must not dismiss the release notes unread. */}
      <DialogContent
        className="sm:max-w-lg p-0 gap-0"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4"
          onClick={() => dismiss('x')}
          data-testid="whats-new-close"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            What's New
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] px-6 py-4">
          <ChangelogContent entries={entries} />
        </ScrollArea>

        <DialogFooter className="px-6 pb-6 pt-2 border-t border-border">
          <button
            type="button"
            onClick={handleReviewLinkClick}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors sm:mr-auto"
            data-testid="whats-new-review-link"
          >
            Enjoying the updates? Leave a review →
          </button>
          <Button onClick={() => dismiss('got_it')} className="w-full sm:w-auto" data-testid="whats-new-got-it">
            Got it
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
